<template>
  <div class="user-wrapper">
    <div class="header-actions">
      <!-- Online/Offline Status Indicator Component -->
      <div class="status-indicator">
        <online-status-indicator />
      </div>

      <!-- Notification Bell -->
      <div class="notification-bell">
        <a-popover
          v-model="visible"
          trigger="click"
          placement="bottomRight"
          overlayClassName="header-notice-wrapper"
          :getPopupContainer="() => $refs.noticeRef.parentElement"
          :autoAdjustOverflow="true"
          :arrowPointAtCenter="true"
          :overlayStyle="{ width: '320px', top: '50px'}"
        >
          <template slot="content">
            <div class="notification-header">
              <h4 style="margin: 0; padding: 8px 0;">Notifications</h4>
            </div>
            <a-list item-layout="vertical" :data-source="items.data" size="small">
              <a-list-item slot="renderItem" key="item.id" slot-scope="item" class="notification-item">
                <template slot="actions">
                  <span :key="item.id" class="notification-time">
                    {{ moment(item.created_at).fromNow() }}
                  </span>
                </template>
                <a-list-item-meta :description="item.data.message">
                  <a slot="title" @click="read(item.id)" class="notification-title">{{ item.data.title }}</a>
                </a-list-item-meta>
              </a-list-item>
            </a-list>
          </template>
          <span @click="toggleVisibility" class="header-notice-trigger" ref="noticeRef">
              <a-badge :count="items.total" :showZero="false">
                <a-icon type="bell" class="bell-icon"/>
              </a-badge>
          </span>
        </a-popover>
      </div>

      <!-- User Dropdown Menu -->
      <div class="user-menu">
        <a-dropdown placement="bottomRight">
          <span class="user-dropdown-trigger">
            <a-avatar size="small" icon="user" class="user-avatar"/>
            <span class="user-info">
              <span class="user-name">{{ user().name }}</span>
              <span v-if="user().branch" class="user-branch">({{ user().branch.name }})</span>
            </span>
            <a-icon type="down" class="dropdown-arrow"/>
          </span>
          <a-menu slot="overlay" class="user-dropdown-menu-wrapper">
            <a-menu-item key="hyperion.user.profile" v-if="backend">
              <a :href="routeUri('hyperion.user.profile', {user_hash: user().hash})">
                <a-icon type="user"/>
                <span>Profile</span>
              </a>
            </a-menu-item>
            <a-menu-divider />
            <a-menu-item key="auth.logout">
              <a :href="routeUri('auth.logout')">
                <a-icon type="logout"/>
                <span>Log Out</span>
              </a>
            </a-menu-item>
          </a-menu>
        </a-dropdown>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  props: {
    backend: {
      required: true,
      type: Boolean,
      default: true
    }
  },
  computed: {
    notificationUri() {
      return (this.backend) ? this.routeUri('hyperion.notification') : this.routeUri('report.notification')
    },
    readNotificationUri() {
      return (this.backend) ? 'hyperion.notification.read' : 'report.notification.read'
    }
  },
  data() {
    return {
      loading: false,
      visible: false,
      items: {}
    }
  },
  mounted() {
    this.items = this.getDefaultPagination()
    this.getItems()
  },
  methods: {
    toggleVisibility() {
      this.visible = !this.visible
    },
    getItems() {
      this.loading = true
      this.$http.get(this.notificationUri, {
        params: {
          unread: 1,
          per_page: 5
        }
      })
        .then((response) => this.items = response.data)
        .catch((error) => this.handleXhrError(error))
        .finally(() => this.loading = false)
    },
    read(id) {
      this.$http.get(this.routeUri(this.readNotificationUri, {notification: id}))
        .then((response) => {})
        .catch((error) => {})
        .finally(() => window.location.reload())
    }
  }
}
</script>

<style lang="less" scoped>
.user-wrapper {
  .header-actions {
    display: flex;
    align-items: center;
    gap: 16px;
    height: 100%;
  }

  .status-indicator {
    display: flex;
    align-items: center;
  }

  .notification-bell {
    display: flex;
    align-items: center;

    .header-notice-trigger {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 8px;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.3s;
      height: 40px;
      width: 40px;

      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }

      .bell-icon {
        font-size: 16px;
        color: #666;

        &:hover {
          color: #1890ff;
        }
      }
    }
  }

  .user-menu {
    display: flex;
    align-items: center;

    .user-dropdown-trigger {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 12px;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.3s;
      border: 1px solid #d9d9d9;
      background: #fff;

      &:hover {
        border-color: #1890ff;
        background-color: rgba(24, 144, 255, 0.02);
      }

      .user-avatar {
        background-color: #1890ff;
      }

      .user-info {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        max-width: 180px;

        .user-name {
          font-size: 14px;
          font-weight: 500;
          color: #333;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 160px;
        }

        .user-branch {
          font-size: 12px;
          color: #666;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 160px;
        }
      }

      .dropdown-arrow {
        font-size: 12px;
        color: #999;
        margin-left: 4px;
      }
    }
  }
}

// Global styles for notification popover
:global(.header-notice-wrapper) {
  .notification-header {
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 8px;

    h4 {
      color: #333;
      font-weight: 600;
    }
  }

  .notification-item {
    padding: 8px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
      border-bottom: none;
    }

    .notification-title {
      color: #333;
      font-weight: 500;

      &:hover {
        color: #1890ff;
      }
    }

    .notification-time {
      font-size: 12px;
      color: #999;
    }
  }
}

// Global styles for user dropdown menu
:global(.user-dropdown-menu-wrapper) {
  min-width: 160px;

  .ant-dropdown-menu-item {
    padding: 8px 16px;

    .anticon {
      margin-right: 8px;
      font-size: 14px;
    }

    &:hover {
      background-color: #f5f5f5;
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .user-wrapper {
    .header-actions {
      gap: 8px;
    }

    .user-menu {
      .user-dropdown-trigger {
        padding: 4px 8px;

        .user-info {
          max-width: 120px;

          .user-name {
            font-size: 13px;
            max-width: 100px;
          }

          .user-branch {
            font-size: 11px;
            max-width: 100px;
          }
        }
      }
    }

    .notification-bell {
      .header-notice-trigger {
        width: 36px;
        height: 36px;
        padding: 6px;
      }
    }
  }
}

@media (max-width: 480px) {
  .user-wrapper {
    .header-actions {
      gap: 4px;
    }

    .user-menu {
      .user-dropdown-trigger {
        .user-info {
          display: none;
        }
      }
    }
  }
}
</style>
